import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Check, Headphones, MessageSquare, Zap } from "lucide-react";

import { DiscordIcon } from "../../../components/icons/BrandIcons";
import { Button } from "../../../components/ui";
import { signIn } from "../../../lib/auth-client";
import { trpc } from "../../../lib/trpc";

export const Route = createFileRoute("/app/onboarding/step-2")({
  component: DiscordConnectStep,
});

const benefits = [
  {
    icon: Headphones,
    title: "Voice Chat",
    description: "Auto-join team voice channels when your match starts.",
  },
  {
    icon: MessageSquare,
    title: "Match Notifications",
    description: "Get pinged when matches are found and results are posted.",
  },
  {
    icon: Zap,
    title: "Faster Setup",
    description: "Clubs can verify your identity and assign roles automatically.",
  },
];

function DiscordConnectStep() {
  const navigate = useNavigate();
  const { data: providers, isLoading } = trpc.user.authProviders.useQuery();

  const isConnected = !isLoading && !!providers?.hasDiscordProvider;

  const handleConnect = () => {
    void signIn.social({
      provider: "discord",
      callbackURL: `${window.location.origin}/app/onboarding/step-2`,
    });
  };

  return (
    <div className="animate-fade-up p-4 md:p-6">
      <div className="relative rounded-2xl bg-surface-900/80 border border-surface-700/50 overflow-hidden">
        {/* Top accent */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#5865F2]/40 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(88,101,242,0.06)_0%,_transparent_60%)] pointer-events-none" />

        <div className="relative px-6 sm:px-8 py-8 space-y-8">
          {/* Header with Discord icon */}
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <div className="absolute -inset-3 rounded-2xl bg-[#5865F2]/15 blur-xl" />
              <div className="relative flex items-center justify-center size-16 rounded-2xl bg-[#5865F2]/10 border border-[#5865F2]/20">
                <DiscordIcon className="size-8 text-[#5865F2]" />
              </div>
            </div>
            <div className="text-center space-y-2">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
                {isConnected ? "Discord Connected" : "Connect Discord"}
              </h1>
              <p className="text-sm text-surface-400">
                {isConnected
                  ? "You're all set! Discord features will be available in your matches."
                  : "Rivaled works best with Discord. Connect now for the full experience."}
              </p>
            </div>
          </div>

          {/* Benefits */}
          <div className="space-y-3">
            {benefits.map((benefit) => (
              <div
                key={benefit.title}
                className="flex items-start gap-4 rounded-xl border border-surface-700/30 bg-surface-800/30 px-4 py-3.5"
              >
                <div className="flex items-center justify-center size-9 rounded-lg bg-[#5865F2]/10 text-[#5865F2] shrink-0 mt-0.5">
                  <benefit.icon className="size-4.5" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-surface-100">{benefit.title}</p>
                  <p className="text-xs text-surface-400 mt-0.5">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Connect / Connected state */}
          {isConnected ? (
            <div className="flex items-center justify-center gap-2 rounded-lg border border-[#5865F2]/30 bg-[#5865F2]/5 py-3 text-sm font-semibold text-[#5865F2]">
              <Check className="size-4" />
              Connected
            </div>
          ) : (
            <>
              <Button
                variant="primary"
                className="w-full !bg-[#5865F2] hover:!bg-[#4752C4] !text-white"
                onClick={handleConnect}
                leftIcon={<DiscordIcon className="size-4" />}
              >
                Connect Discord
              </Button>
              <p className="text-xs text-surface-500 text-center">
                You can always connect Discord later in Settings
              </p>
            </>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="ghost"
              onClick={() => void navigate({ to: "/app/onboarding/step-1" })}
              leftIcon={<ArrowLeft className="size-4" />}
            >
              Back
            </Button>
            <Button
              variant="primary"
              onClick={() => void navigate({ to: "/app/onboarding/step-3" })}
              rightIcon={<ArrowRight className="size-4" />}
            >
              {isConnected ? "Next" : "Skip for now"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
